
import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { posthog, initPostHog } from '@/lib/posthog';
import { ANALYTICS_CONFIG, REDDIT_EVENTS } from '@/lib/analytics-config';

interface AnalyticsContextType {
  trackEvent: (event: string, properties?: Record<string, any>) => void;
  identifyUser: (userId: string, traits?: Record<string, any>) => void;
  trackPageView: (path: string, title?: string) => void;
  trackFormSubmission: (formName: string, properties?: Record<string, any>) => void;
  trackButtonClick: (buttonName: string, location: string, properties?: Record<string, any>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  useEffect(() => {
    // PostHog temporarily disabled
    // initPostHog();
  }, []);

  const trackRedditEvent = (eventType: string, properties?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.rdt) {
      const eventData = {
        page_path: window.location.pathname,
        page_location: window.location.href,
        timestamp: new Date().toISOString(),
        ...properties
      };
      
      // Track custom event or standard event
      if (eventType === REDDIT_EVENTS.CUSTOM && properties?.customEventName) {
        window.rdt('track', 'Custom', { customEventName: properties.customEventName, ...eventData });
      } else {
        window.rdt('track', eventType, eventData);
      }
    }
  };

  const trackEvent = (event: string, properties?: Record<string, any>) => {
    // PostHog temporarily disabled
    // posthog.capture(event, properties);
    
    // Push to dataLayer for GTM/GA4
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event,
        ...properties
      });
    }
  };

  const identifyUser = (userId: string, traits?: Record<string, any>) => {
    // PostHog temporarily disabled
    // posthog.identify(userId, traits);
  };

  const trackPageView = (path: string, title?: string) => {
    // PostHog temporarily disabled
    // posthog.capture('$pageview', {
    //   $current_url: window.location.href,
    //   page_title: title || document.title,
    //   page_path: path
    // });

    // Push to dataLayer for GTM/GA4
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: path,
        page_title: title || document.title,
        page_location: window.location.href
      });
    }

    // Track high-intent pages in Reddit
    if (path === '/pricing' || path.startsWith('/pricing')) {
      trackRedditEvent(REDDIT_EVENTS.VIEW_CONTENT, {
        content_type: 'pricing_page',
        source: 'page_view'
      });
    }
  };

  const trackFormSubmission = (formName: string, properties?: Record<string, any>) => {
    trackEvent('form_submitted', {
      form_name: formName,
      ...properties
    });

    // Track all form submissions as Leads in Reddit
    trackRedditEvent(REDDIT_EVENTS.LEAD, {
      lead_type: formName,
      source: 'form_submission',
      ...properties
    });
  };

  const trackButtonClick = (buttonName: string, location: string, properties?: Record<string, any>) => {
    trackEvent('button_clicked', {
      button_name: buttonName,
      location,
      ...properties
    });

    // Track specific CTA clicks in Reddit
    const ctaType = properties?.cta_type;
    if (ctaType === 'primary' || buttonName.toLowerCase().includes('get started') || buttonName.toLowerCase().includes('sign up')) {
      trackRedditEvent(REDDIT_EVENTS.CUSTOM, {
        customEventName: 'cta_clicked',
        button_name: buttonName,
        location,
        source: 'button_click',
        ...properties
      });
    }
  };

  const value = {
    trackEvent,
    identifyUser,
    trackPageView,
    trackFormSubmission,
    trackButtonClick
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
